import { DEFAULT_LONG_TEXT_MAX, DEFAULT_TEXT_MAX, e164Schema, emailSchema, } from "@wapadesk/schema";
const isEmpty = (v) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
/** Validates and normalises an answer for a node. Pure; server and client share it. */
export function validateAnswer(node, raw, opts = {}) {
    if (node.type === "message")
        return { ok: false, code: "no_answer_expected" };
    if (isEmpty(raw))
        return node.required ? { ok: false, code: "required" } : { ok: true, value: "" };
    switch (node.type) {
        case "consent": {
            if (typeof raw !== "object" || raw === null || Array.isArray(raw))
                return { ok: false, code: "invalid_type" };
            const items = raw;
            const value = {};
            for (const item of node.items) {
                const v = items[item.key] === true;
                if (item.required && !v)
                    return { ok: false, code: "consent_required", detail: item.key };
                value[item.key] = v;
            }
            return { ok: true, value };
        }
        case "text":
        case "long_text": {
            if (typeof raw !== "string")
                return { ok: false, code: "invalid_type" };
            const max = node.maxLength ?? (node.type === "text" ? DEFAULT_TEXT_MAX : DEFAULT_LONG_TEXT_MAX);
            const value = raw.trim();
            if (value.length > max)
                return { ok: false, code: "too_long", detail: String(max) };
            if (node.required && value === "")
                return { ok: false, code: "required" };
            return { ok: true, value };
        }
        case "email": {
            if (typeof raw !== "string")
                return { ok: false, code: "invalid_type" };
            const value = raw.trim().toLowerCase();
            return emailSchema.safeParse(value).success
                ? { ok: true, value }
                : { ok: false, code: "invalid_email" };
        }
        case "phone": {
            if (typeof raw !== "string")
                return { ok: false, code: "invalid_type" };
            const value = raw.replace(/[\s()-]/g, "");
            return e164Schema.safeParse(value).success
                ? { ok: true, value }
                : { ok: false, code: "invalid_phone" };
        }
        case "number": {
            const n = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw.trim()) : NaN;
            if (!Number.isFinite(n))
                return { ok: false, code: "not_a_number" };
            if ((node.min !== undefined && n < node.min) || (node.max !== undefined && n > node.max))
                return { ok: false, code: "out_of_range" };
            return { ok: true, value: n };
        }
        case "date": {
            if (typeof raw !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(raw))
                return { ok: false, code: "invalid_date" };
            const t = Date.parse(raw + "T00:00:00Z");
            if (Number.isNaN(t))
                return { ok: false, code: "invalid_date" };
            if ((node.minDate && raw < node.minDate) || (node.maxDate && raw > node.maxDate))
                return { ok: false, code: "out_of_range" };
            return { ok: true, value: raw };
        }
        case "single_choice": {
            if (typeof raw !== "string")
                return { ok: false, code: "invalid_type" };
            const ids = new Set(node.options.map((o) => o.id));
            if (ids.has(raw))
                return { ok: true, value: raw };
            if (node.allowOther && raw.startsWith("other:") && raw.length <= 6 + DEFAULT_TEXT_MAX)
                return { ok: true, value: raw };
            return { ok: false, code: "invalid_option" };
        }
        case "multi_choice": {
            if (!Array.isArray(raw) || !raw.every((x) => typeof x === "string"))
                return { ok: false, code: "invalid_type" };
            const ids = new Set(node.options.map((o) => o.id));
            const value = [...new Set(raw)];
            for (const v of value) {
                if (ids.has(v))
                    continue;
                if (node.allowOther && v.startsWith("other:") && v.length <= 6 + DEFAULT_TEXT_MAX)
                    continue;
                return { ok: false, code: "invalid_option", detail: v };
            }
            if (node.maxSelections !== undefined && value.length > node.maxSelections)
                return { ok: false, code: "too_many" };
            return { ok: true, value };
        }
        case "procedure_picker": {
            const list = Array.isArray(raw) ? raw : [raw];
            if (!list.every((x) => typeof x === "string"))
                return { ok: false, code: "invalid_type" };
            const value = [...new Set(list)];
            if (!node.multiple && value.length > 1)
                return { ok: false, code: "too_many" };
            if (opts.procedureIds)
                for (const v of value)
                    if (!opts.procedureIds.has(v))
                        return { ok: false, code: "invalid_option", detail: v };
            return { ok: true, value };
        }
        case "file_upload": {
            if (!Array.isArray(raw) || !raw.every((x) => typeof x === "string"))
                return { ok: false, code: "invalid_type" };
            const value = [...new Set(raw)];
            if (value.length > node.maxFiles)
                return { ok: false, code: "too_many", detail: String(node.maxFiles) };
            return { ok: true, value };
        }
        case "booking":
        case "payment":
            return { ok: false, code: "invalid_type", detail: "phase 2" };
    }
}
//# sourceMappingURL=validate.js.map